import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { FaPlus, FaLayerGroup } from 'react-icons/fa';
import { useToast } from '../contexts/ToastContext';
import { Button } from '../components/ui/button';
import AddProjectGroupModal from '../components/project-groups/AddProjectGroupModal';
import EditProjectGroupModal from '../components/project-groups/EditProjectGroupModal';
import DeleteProjectGroupModal from '../components/project-groups/DeleteProjectGroupModal';
import ProjectGroupCard from '../components/project-groups/ProjectGroupCard';
import AddProjectModal from '../components/projects/AddProjectModal';
import { useAuth } from '../hooks/useAuth';

function ProjectGroups() {
  const [projectGroups, setProjectGroups] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const { user } = useAuth();
  const addToast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjectGroups = async () => {
      try {
        // Only fetch if we have a user
        if (!user) return;
        
        const { data, error } = await supabase
          .from('project_groups')
          .select(`
            *,
            projects(count),
            child_groups:project_groups!parent_project_group_id(count)
          `)
          .eq('user_id', user.id)
          .order('is_favorite', { ascending: false })
          .order('name', { ascending: true });
        if (error) throw error;
        console.log('Fetched data:', data);
        setProjectGroups(data || []);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProjectGroups();
  }, [user]); // Keep user in dependencies

  // Update loading state check to also check for user
  if (!user || loading) return <p>Loading...</p>;
  if (error) return <p>Failed: {error}</p>;
  if (!Array.isArray(projectGroups)) return <p>No project groups found.</p>;

  const handleAddProjectGroup = () => {
    setShowAddModal(true);
  };

  const handleEditProjectGroup = (group) => {
    setSelectedGroup(group);
    setShowEditModal(true);
  };

  const handleDeleteProjectGroup = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const handleAddProject = (groupId) => {
    setSelectedGroupId(groupId);
    setShowAddProjectModal(true);
  };

  const toggleFavorite = async (groupId, isFavorite) => {
    const { error } = await supabase
      .from('project_groups')
      .update({ is_favorite: !isFavorite })
      .eq('id', groupId)
      .single();

    if (error) {
      console.error('Error updating favorite status:', error);
      addToast('Error updating favorite status: ' + error.message, 'danger');
    } else {
      setProjectGroups((prev) =>
        prev
          .map((group) => (group.id === groupId ? { ...group, is_favorite: !isFavorite } : group))
          .sort((a, b) => {
            if (a.is_favorite === b.is_favorite) {
              return a.name.localeCompare(b.name);
            }
            return b.is_favorite - a.is_favorite;
          })
      );
      addToast('Favorite status updated successfully', 'success');
    }
  };

  const toggleArchive = async (groupId, isArchived) => {
    const { error } = await supabase
      .from('project_groups')
      .update({ is_archived: !isArchived })
      .eq('id', groupId)
      .single();

    if (error) {
      console.error('Error updating archive status:', error);
      addToast('Error updating archive status: ' + error.message, 'danger');
    } else {
      setProjectGroups((prev) =>
        prev.map((group) => 
          group.id === groupId ? { ...group, is_archived: !isArchived } : group
        )
      );
      addToast('Archive status updated successfully', 'success');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="text-red-800 dark:text-red-200">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <FaLayerGroup className="w-6 h-6 mr-3 text-red-600" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Project Groups
          </h1>
        </div>
        <Button onClick={handleAddProjectGroup} className="flex items-center space-x-2">
          <FaPlus className="w-4 h-4" />
          <span>Add Project Group</span>
        </Button>
      </div>

      {/* Project Groups Grid */}
      {projectGroups.length === 0 ? (
        <div className="text-center py-12">
          <FaLayerGroup className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No project groups yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Get started by creating your first project group to organize your work.
          </p>
          <Button onClick={handleAddProjectGroup}>
            <FaPlus className="w-4 h-4 mr-2" />
            Create Project Group
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projectGroups.map((group) => (
            <ProjectGroupCard
              key={group.id}
              projectGroup={group}
              viewMode="card"
              onEdit={handleEditProjectGroup}
              onDelete={handleDeleteProjectGroup}
              onAddProject={handleAddProject}
              onToggleFavorite={toggleFavorite}
              onToggleArchive={toggleArchive}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddProjectGroupModal
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          userId={user.id}
          onProjectGroupAdded={(newGroup) => setProjectGroups([...projectGroups, newGroup])}
        />
      )}
      {showEditModal && (
        <EditProjectGroupModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          item={selectedGroup}
          onItemUpdated={(updatedGroup) => {
            setProjectGroups((prev) =>
              prev.map((group) => (group.id === updatedGroup.id ? updatedGroup : group))
            );
            setShowEditModal(false);
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteProjectGroupModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          item={selectedGroup}
          onItemDeleted={(deletedGroupId) =>
            setProjectGroups((prev) => prev.filter((group) => group.id !== deletedGroupId))
          }
        />
      )}
      {showAddProjectModal && (
        <AddProjectModal
          show={showAddProjectModal}
          handleClose={() => setShowAddProjectModal(false)}
          projectGroupId={selectedGroupId}
          onProjectAdded={(newProject) =>
            setProjectGroups((prev) =>
              prev.map((group) =>
                group.id === selectedGroupId
                  ? { ...group, projects: [...group.projects, newProject] }
                  : group
              )
            )
          }
        />
      )}
    </div>
  );
}

export default ProjectGroups;
